// ── 미팅지도 ⚙️ 설정 '마커 표시' 탭 상태 (2026-10-24) ──────────────────────────
// 읽기·저장 = /api/admin/meeting-marker-config (테넌트 전체, 서버 services/tm/meeting-marker-config.js).
// 저장하면 같은 화면의 지도에 바로 알린다(MARKER_CONFIG_EVENT → useMarkerConfig).
import { useCallback, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { defaultMarkerConfig, normalizeMarkerConfig } from '../../../components/meetingmap/markerConfig';
import { MARKER_CONFIG_EVENT } from '../../../components/meetingmap/useMarkerConfig';

export function useMarkerSettings() {
  const { showToast } = useToast();
  const [config, setConfig] = useState(defaultMarkerConfig);
  const [saved, setSaved] = useState(null); // 마지막으로 저장(또는 불러온) 값 — '아직 저장 전' 표시용
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    const r = await apiClient.get('/api/admin/meeting-marker-config');
    setLoading(false);
    if (!r.ok) { showToast(r.data?.message || '마커 설정을 불러오지 못했습니다.', 'error'); return; }
    const c = normalizeMarkerConfig(r.data?.config);
    setConfig(c);
    setSaved(c);
    setCategories(Array.isArray(r.data?.categories) ? r.data.categories : []);
  }, [showToast]);

  async function save() {
    setSaving(true);
    const r = await apiClient.put('/api/admin/meeting-marker-config', { config });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '마커 설정을 저장하지 못했습니다.', 'error'); return; }
    const c = normalizeMarkerConfig(r.data?.config);
    setConfig(c);
    setSaved(c);
    window.dispatchEvent(new CustomEvent(MARKER_CONFIG_EVENT, { detail: c }));
    showToast('마커 표시를 저장했습니다.', 'success');
  }

  const patchPart = (key, patch) => setConfig((p) => ({
    ...p, parts: p.parts.map((x) => (x.key === key ? { ...x, ...patch } : x)),
  }));
  const movePart = (key, dir) => setConfig((p) => {
    const i = p.parts.findIndex((x) => x.key === key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= p.parts.length) return p;
    const parts = [...p.parts];
    [parts[i], parts[j]] = [parts[j], parts[i]];
    return { ...p, parts };
  });
  // 카테고리별 표시 글자 — 1~3글자, 비우면 지운다(그 카테고리는 위 규칙: 앞 1글자/이름 전체)
  const setLetter = (cat, text) => setConfig((p) => {
    const next = { ...p.categoryLetters };
    const l = [...String(text || '')].slice(0, 3).join('');
    if (l.trim()) next[cat] = l; else delete next[cat];
    return { ...p, categoryLetters: next };
  });
  // 카테고리 색 — 고르지 않으면 파랑(마커 색은 카테고리 색뿐)
  const setCategoryColor = (cat, tone) => setConfig((p) => {
    const next = { ...p.categoryColors };
    if (tone) next[cat] = tone; else delete next[cat];
    return { ...p, categoryColors: next };
  });
  const resetDefault = () => setConfig(defaultMarkerConfig());
  const dirty = saved !== null && JSON.stringify(normalizeMarkerConfig(config)) !== JSON.stringify(saved);

  return {
    config, categories, loading, saving, dirty,
    fetchConfig, save, patchPart, movePart, setLetter, setCategoryColor, resetDefault,
  };
}
